import React from "react";
import { Alert, Input, Spin, Tag, message } from "antd";
import { saveAs } from "file-saver";
import CommonButton from "../../common/button/button.js";
import ModalView from "../../common/modal/index.js";
import Text from "../../common/typography/text.js";
import {
  ACCOUNT_CONTACTS_SECTION,
  ACCOUNT_ENROLLMENT_FIELDS,
  ACCOUNT_ENROLLMENT_SECTIONS,
  formatAccountContactName,
} from "../AccountEnrollment/accountEnrollmentFields.js";
import {
  DISCOUNT_MODES,
  OFFER_TYPES,
  describeOfferType,
} from "../AccountEnrollment/accountEnrollmentPricing.js";
import {
  ENROLLMENT_ERROR_CODES,
  getAccountEnrollmentRequestById,
  getEnrollmentBaaDownloadUrl,
  getEnrollmentErrorCode,
  getEnrollmentErrorMessage,
  reviewAccountEnrollmentRequest,
} from "../AccountEnrollment/accountEnrollmentService.js";
import { createAccountEnrollmentPdfBlob } from "./AccountEnrollmentPdf.js";
import "../AccountEnrollment/AccountEnrollment.css";

const displayValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized || "—";
};

const formatPercent = (value) =>
  value === null || value === undefined ? "—" : `${value}%`;

const formatCurrency = (value) =>
  value === null || value === undefined
    ? "—"
    : Number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });

const AccountEnrollmentReviewModal = ({
  requestId,
  open,
  canReview,
  onClose,
  onReviewed,
}) => {
  const [detail, setDetail] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [rejectMode, setRejectMode] = React.useState(false);
  const [rejectionReason, setRejectionReason] = React.useState("");
  const [downloadingPdf, setDownloadingPdf] = React.useState(false);
  const [downloadingBaa, setDownloadingBaa] = React.useState(false);

  const loadDetail = React.useCallback(async () => {
    if (!open || !requestId) return;
    setLoading(true);
    setError("");
    try {
      setDetail(await getAccountEnrollmentRequestById(requestId));
    } catch (requestError) {
      setDetail(null);
      setError(
        getEnrollmentErrorMessage(
          requestError,
          "Unable to load the account enrollment request.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [open, requestId]);

  React.useEffect(() => {
    if (!open) return;
    setDetail(null);
    setRejectMode(false);
    setRejectionReason("");
    loadDetail();
  }, [loadDetail, open]);

  const pending = detail?.Status === "Pending";
  const pricing = detail?.Pricing;
  const account = detail?.Account || {};
  const contacts = detail?.AccountContacts || [];

  const completeReview = async (action) => {
    if (!detail?.ID || saving) return;
    setSaving(true);
    setError("");
    try {
      await reviewAccountEnrollmentRequest({
        id: detail.ID,
        action,
        rejectionReason,
      });
      message.success(
        action === "approve"
          ? "Account enrollment approved."
          : "Account enrollment rejected.",
      );
      onReviewed?.();
      onClose?.();
    } catch (requestError) {
      // Someone else reviewed it first: reload so the modal shows the
      // final state instead of stale Approve/Reject buttons (plan 9.5).
      if (
        getEnrollmentErrorCode(requestError) ===
        ENROLLMENT_ERROR_CODES.ALREADY_REVIEWED
      ) {
        await loadDetail();
        onReviewed?.();
        setRejectMode(false);
      }
      setError(
        getEnrollmentErrorMessage(
          requestError,
          "Unable to review this request.",
        ),
      );
    } finally {
      setSaving(false);
    }
  };

  const downloadPdf = async () => {
    if (!detail || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const blob = await createAccountEnrollmentPdfBlob(detail);
      const accountName = String(detail.Account?.AccountName || "")
        .trim()
        .replace(/[^a-zA-Z0-9_-]+/g, "_")
        .replace(/^_+|_+$/g, "");
      saveAs(
        blob,
        `Account_Enrollment_${detail.ID}${accountName ? `_${accountName}` : ""}.pdf`,
      );
    } catch (error) {
      message.error("Unable to generate the PDF. Please try again.");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadBaa = async () => {
    if (!detail?.BAAAzureBlobName || downloadingBaa) return;
    setDownloadingBaa(true);
    try {
      const url = await getEnrollmentBaaDownloadUrl(detail.BAAAzureBlobName);
      if (!url) {
        message.error("The BAA file could not be found.");
        return;
      }
      window.open(url, "_blank", "noopener");
    } catch (error) {
      message.error(getEnrollmentErrorMessage(error, "Unable to download the BAA file."));
    } finally {
      setDownloadingBaa(false);
    }
  };

  const reject = () => {
    if (!rejectionReason.trim()) {
      setError("A rejection reason is required.");
      return;
    }
    completeReview("reject");
  };

  const renderPricing = () => {
    if (!pricing) return null;

    if (pricing.OfferType === OFFER_TYPES.PRICE_PER_SQUARE) {
      return (
        <table className="account-enrollment-review-table">
          <thead>
            <tr>
              <th>Product Group</th>
              <th>Manufacturer</th>
              <th>Q Code</th>
              <th>Price Per Square</th>
            </tr>
          </thead>
          <tbody>
            {(pricing.ProductGroups || []).map((group) => (
              <tr key={group.ProductGroupID}>
                <td>{group.ProductGroupName}</td>
                <td>{group.ManufacturerName}</td>
                <td>{displayValue(group.QCode)}</td>
                <td>{formatCurrency(group.PricePerSquare)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    if (pricing.DiscountMode === DISCOUNT_MODES.DEFAULT_FOR_ALL) {
      return (
        <Text variant="smallText">
          {formatPercent(pricing.DefaultDiscountPercentage)} applied to all{" "}
          {(pricing.ProductGroups || []).length} selected product group
          {(pricing.ProductGroups || []).length === 1 ? "" : "s"}.
        </Text>
      );
    }

    if (pricing.DiscountMode === DISCOUNT_MODES.PER_PRODUCT_GROUP) {
      return (
        <table className="account-enrollment-review-table">
          <thead>
            <tr>
              <th>Product Group</th>
              <th>Manufacturer</th>
              <th>Q Code</th>
              <th>Discount</th>
            </tr>
          </thead>
          <tbody>
            {(pricing.ProductGroups || []).map((group) => (
              <tr key={group.ProductGroupID}>
                <td>{group.ProductGroupName}</td>
                <td>{group.ManufacturerName}</td>
                <td>{displayValue(group.QCode)}</td>
                <td>{formatPercent(group.DiscountPercentage)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return (
      <table className="account-enrollment-review-table">
        <thead>
          <tr>
            <th>Product Group</th>
            <th>Product Code</th>
            <th>Units</th>
            <th>Standard Price</th>
            <th>Discount</th>
          </tr>
        </thead>
        <tbody>
          {(pricing.ProductGroups || []).flatMap((group) =>
            (group.Products || []).map((product) => (
              <tr key={`${group.ProductGroupID}-${product.ProductID}`}>
                <td>{group.ProductGroupName}</td>
                <td>{product.ProductCode}</td>
                <td>{displayValue(product.Units)}</td>
                <td>{formatCurrency(product.StandardPrice)}</td>
                <td>{formatPercent(product.DiscountPercentage)}</td>
              </tr>
            )),
          )}
        </tbody>
      </table>
    );
  };

  const reviewContent = (
    <>
      {error ? <Alert message={error} showIcon type="error" /> : null}
      {loading ? (
        <div className="account-enrollment-empty" data-testid="review-loading">
          <Spin />
        </div>
      ) : null}
      {detail ? (
        <div className="account-enrollment-review">
          <div className="account-enrollment-review-meta">
            <div>
              <Text color="lightText" variant="tinyText">
                Account
              </Text>
              <Text variant="smallText">
                {displayValue(account.AccountName)}
              </Text>
            </div>
            <div>
              <Text color="lightText" variant="tinyText">
                Requester
              </Text>
              <Text variant="smallText">
                {displayValue(detail.RequesterName)}
              </Text>
            </div>
            <div>
              <Text color="lightText" variant="tinyText">
                Status
              </Text>
              <span>
                <Tag>{detail.Status}</Tag>
              </span>
            </div>
          </div>

          {ACCOUNT_ENROLLMENT_SECTIONS.map((section) => (
            <section key={section.key}>
              <Text color="lightText" variant="tinyText">
                {section.title}
              </Text>
              <div className="account-enrollment-review-grid">
                {ACCOUNT_ENROLLMENT_FIELDS.filter(
                  (field) => field.section === section.key,
                ).map((field) => (
                  <div
                    className="account-enrollment-review-item"
                    key={field.key}
                  >
                    <Text color="lightText" variant="tinyText">
                      {field.label}
                    </Text>
                    <Text variant="smallText">
                      {field.key === "DistributorID"
                        ? displayValue(detail.DistributorName)
                        : field.key === "RegionID"
                          ? displayValue(detail.RegionName ?? account.RegionID)
                          : displayValue(account[field.key])}
                    </Text>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section data-testid="review-account-contacts">
            <Text color="lightText" variant="tinyText">
              {ACCOUNT_CONTACTS_SECTION.title} ({contacts.length})
            </Text>
            {contacts.length ? (
              <>
                <div className="account-enrollment-review-table-wrap">
                  <table className="account-enrollment-review-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Provider</th>
                        <th>Provider NPI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {contacts.map((contact) => (
                        <tr key={contact.Email}>
                          <td>{displayValue(formatAccountContactName(contact))}</td>
                          <td>{displayValue(contact.Email)}</td>
                          <td>{displayValue(contact.Phone)}</td>
                          <td>{contact.IsProvider ? "Yes" : "No"}</td>
                          <td>{contact.IsProvider ? displayValue(contact.ProviderNPI) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {pending ? (
                  <Text color="lightText" variant="tinyText">
                    On approval these users are added to the new parent account. They are not invited to Tissue Vault.
                  </Text>
                ) : null}
              </>
            ) : (
              <Text variant="smallText">No users requested</Text>
            )}
          </section>

          <section>
            <Text color="lightText" variant="tinyText">
              Business Associate Agreement
            </Text>
            {detail.BAAFileName ? (
              <div className="account-enrollment-review-baa">
                <Text variant="smallText">{detail.BAAFileName}</Text>
                <CommonButton
                  disabled={downloadingBaa}
                  onClick={downloadBaa}
                  variant="link"
                >
                  {downloadingBaa ? "Opening…" : "Download BAA"}
                </CommonButton>
              </div>
            ) : (
              <Text variant="smallText">No BAA attached</Text>
            )}
          </section>

          <section>
            <Text color="lightText" variant="tinyText">
              Selected product groups ({(detail.ProductGroups || []).length})
            </Text>
            <div className="account-enrollment-review-table-wrap">
              <table className="account-enrollment-review-table">
                <thead>
                  <tr>
                    <th>Product Group</th>
                    <th>Manufacturer</th>
                    <th>Q Code</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.ProductGroups || []).map((group) => (
                    <tr key={group.ProductGroupID}>
                      <td>{group.ProductGroupName}</td>
                      <td>{group.ManufacturerName}</td>
                      <td>{displayValue(group.QCode)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <Text color="lightText" variant="tinyText">
              Pricing — {describeOfferType(pricing)}
            </Text>
            <div className="account-enrollment-review-table-wrap">
              {renderPricing()}
            </div>
          </section>

          {detail.Status === "Rejected" && detail.RejectionReason ? (
            <Alert
              description={detail.RejectionReason}
              message="Rejection reason"
              type="warning"
            />
          ) : null}

          {canReview && pending && rejectMode ? (
            <div className="account-enrollment-reject-box">
              <label htmlFor="account-enrollment-rejection-reason">
                Rejection reason
              </label>
              <Input.TextArea
                id="account-enrollment-rejection-reason"
                maxLength={2000}
                onChange={(event) => setRejectionReason(event.target.value)}
                rows={4}
                showCount
                value={rejectionReason}
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );

  const reviewActions = (
    <div className="account-enrollment-review-actions">
      <CommonButton
        disabled={saving}
        onClick={onClose}
        style={{ minWidth: "100px" }}
        variant="table"
      >
        Close
      </CommonButton>
      {canReview && detail ? (
        <CommonButton
          disabled={downloadingPdf}
          onClick={downloadPdf}
          style={{ minWidth: "130px" }}
          variant="table"
        >
          {downloadingPdf ? "Preparing…" : "Download PDF"}
        </CommonButton>
      ) : null}
      {canReview && pending && !rejectMode ? (
        <>
          <CommonButton
            disabled={saving}
            onClick={() => setRejectMode(true)}
            style={{ minWidth: "100px" }}
            subVariant="failure"
            variant="table"
          >
            Reject
          </CommonButton>
          <CommonButton
            disabled={saving}
            onClick={() => completeReview("approve")}
            style={{ minWidth: "100px" }}
            subVariant="success"
            variant="table"
          >
            {saving ? "Approving…" : "Approve"}
          </CommonButton>
        </>
      ) : null}
      {canReview && pending && rejectMode ? (
        <>
          <CommonButton
            disabled={saving}
            onClick={() => setRejectMode(false)}
            style={{ minWidth: "100px" }}
            variant="table"
          >
            Back
          </CommonButton>
          <CommonButton
            disabled={!rejectionReason.trim() || saving}
            onClick={reject}
            style={{ minWidth: "140px" }}
            subVariant="failure"
            variant="table"
          >
            {saving ? "Rejecting…" : "Confirm Rejection"}
          </CommonButton>
        </>
      ) : null}
    </div>
  );

  return (
    <ModalView
      footerButtonWrapper={reviewActions}
      isOpen={open}
      maxWidth="min(900px, calc(100vw - 32px))"
      minWidth="min(900px, calc(100vw - 32px))"
      modalSubTitle="Review the submitted account, product groups and agreed pricing."
      modalTitle="Account Enrollment Request"
      onClose={saving ? () => {} : onClose}
      parentClassname="account-enrollment-review-modal"
      textInputComponent={reviewContent}
    />
  );
};

export default AccountEnrollmentReviewModal;
